'use client';
import type { VerticalProductTabItemProps } from '@weberus/ui/components';
import type { Swiper as SwiperCore } from 'swiper';
import type { ConvertedProductTabWidgetData } from '@weberus/shared-types';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import { useRef, useState } from 'react';

import { SubHeader } from '@yanolja-rnd/nol-design-system';
import { clientLogStandardized } from '@weberus/client-tracker';
import { omit } from 'lodash-es';
import { useAtomValue } from 'jotai';
import { ShowAllButton } from 'app/_components/common/show-all-button';
import { pcWidthAtom } from '@/app/_shared/store/device';
import { ProductList } from './product-list';
import { TabArea } from './tab-area';

export interface ProductTabProps {
  data: ConvertedProductTabWidgetData;
  pageNameLog: HomeLogPageNameValue;
}

/**
 *  상품형 탭 위젯
 *
 *  @see {@link https://www.figma.com/design/zlHuiOHmYv1ghVDbTHUSvW/%5BGUIDE%5D-%EC%9C%84%EC%A0%AF%2F%EC%98%A4%EB%8D%94%EB%A7%81?node-id=3235-21482&m=dev}
 */
export const ProductTab: React.FC<ProductTabProps> = ({ data, pageNameLog }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { header, items, logMeta: widgetLogMeta } = data;
  const { isPcWidth } = useAtomValue(pcWidthAtom);
  const productTabItems: VerticalProductTabItemProps[][] = items.map((item) => item.items);
  // 탭 별 logMeta
  const productTabLogMeta = items.map((item) => item.logMeta);
  const swiperRef = useRef<SwiperCore | null>(null);

  const tabLabels = items
    .map((item) => item.header?.title)
    .filter((title): title is string => title !== undefined)
    .flat();
  const hasTab = tabLabels.length > 0;

  const isEachTabHasMore = hasTab && items[selectedIndex]?.header?.more;
  const moreObj = isEachTabHasMore ? items[selectedIndex]?.header?.more : header?.more;
  const moreTitle = moreObj?.title;
  const moreHref = moreObj?.action?.web;

  const generateLogData = (index: number) => {
    return {
      ...widgetLogMeta,
      ...productTabLogMeta?.[index],
    };
  };

  const handleClientLog = (index: number, eventType: 'click' | 'impression') => {
    const logData = generateLogData(index);
    clientLogStandardized(eventType, pageNameLog, logData);
  };

  /** Tab 레이아웃에서 선택 시 발생*/
  const handleChangeTab = (index: number) => {
    setSelectedIndex(index);
    isPcWidth ? swiperRef.current?.slideToLoop(index) : swiperRef.current?.slideTo(index);
    handleClientLog(index, 'click');
  };

  /** 전체보기 선택 시 발생 */
  const handleClickMore = () => {
    clientLogStandardized('click', pageNameLog, {
      ...omit(widgetLogMeta, 'deusGroupId'),
      ...productTabLogMeta,
      ...header?.more?.logMeta,
    });
  };

  const handleChangeSlide = (index: number) => {
    setSelectedIndex(index);
  };

  return (
    <div className="my-16 w-full pc:my-40 pc:w-auto pc:max-w-pc-size">
      {!!header && <SubHeader className="px-16" title={header.title} subTitle={header.subTitle} />}
      {!!hasTab && <TabArea tabLabels={tabLabels} selectedIndex={selectedIndex} onChangeTab={handleChangeTab} />}
      <ProductList
        pageNameLog={pageNameLog}
        tabItems={productTabItems}
        swiperRef={swiperRef}
        widgetLogMeta={widgetLogMeta || {}}
        tabLogMeta={productTabLogMeta || {}}
        onSlideChange={handleChangeSlide}
      />
      {!!moreTitle && !!moreHref && (
        <ShowAllButton
          boldTitle={isEachTabHasMore ? items[selectedIndex].header?.title : undefined}
          title={moreTitle}
          href={moreHref}
          onClick={handleClickMore}
        />
      )}
    </div>
  );
};
